import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Menu, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const statusList = [
    "active",
    "suspended",
    "completed",
    "pending",
    "canceled",
    "defaulter",
    "reactivated",
    "inactive", 
    "disabled",
    "renewed"
]
const statusListContract = [
    "active"
]

const translateStatus = {
    "active": "Activo",
    "suspended": "Suspendido",
    "completed": "Completado",
    "pending": "Pendiente",
    "canceled": "Cancelado",
    "defaulter": "Moroso",
    "reactivated": "Reactivado",
    "inactive": "Inactivo",
    "renewed": "Renovado",
    "no_show": "No asistió",
    "in_review": "En revisión",
    "invalid": "Inválido",
    "confirmed": "Confirmado",
    "disabled": "Desactivado",
    "rescheduled": "Reagendada"
}

const statusStyles = {
    "active": "bg-green-100 hover:bg-green-200 disabled:hover:bg-green-100 text-green-800", 
    "suspended": "bg-purple-100 hover:bg-purple-200 disabled:hover:bg-purple-100 text-purple-800",
    "completed": "bg-blue-100 hover:bg-blue-200 disabled:hover:bg-blue-100 text-v2-blue-text-login",
    "renewed": "bg-blue-100 hover:bg-blue-200 disabled:hover:bg-blue-100 text-v2-blue-text-login",
    "pending": "bg-yellow-100 hover:bg-yellow-200 disabled:hover:bg-yellow-100 text-yellow-800",
    "canceled": "bg-red-100 hover:bg-red-200 disabled:hover:bg-red-100 text-red-800",
    "defaulter": "bg-orange-100 hover:bg-orange-200 disabled:hover:bg-orange-100 text-orange-800",
    "reactivated": "bg-green-100 hover:bg-green-200 disabled:hover:bg-green-100 text-green-800",
    "inactive": "bg-yellow-100 hover:bg-yellow-200 disabled:hover:bg-yellow-100 text-yellow-800",
    "no_show": "bg-amber-100 hover:bg-amber-200 disabled:hover:bg-amber-100 text-amber-800",
    "in_review": "bg-yellow-100 hover:bg-yellow-200 disabled:hover:bg-yellow-100 text-yellow-800",
    "invalid": "bg-red-100 hover:bg-red-200 disabled:hover:bg-red-100 text-red-800",
    "confirmed": "bg-green-100 hover:bg-green-200 disabled:hover:bg-green-100 text-green-800",
    "disabled": "bg-gray-100 hover:bg-gray-200 disabled:hover:bg-gray-100 text-gray-800",
    "rescheduled": "bg-green-100 hover:bg-green-200 disabled:hover:bg-green-100 text-green-800"
}

const itemsPositionList = ["top", "bottom"]

const itemsPositionValues = {
    "bottom": "origin-top-right",
    "top": "bottom-14"
}

function StatusMenuButton({ status, onChange, isContract, disabled, itemsPosition, customStatus, updateStatus}) {

    const [currentStatus, setCurrentStatus] = useState(status);
    const statusArray = customStatus || statusList

    useEffect(() => {
        if (statusArray.includes(status)) setCurrentStatus(status);
    }, [status]);

    const handleStatusChange = (status) => {
        if (updateStatus) {
            updateStatus(status)
            return
        }
        setCurrentStatus(status)
        if (onChange) onChange(status);
    }

    return (
        <div className="z-20 flex">
            <Menu as="div" className="-ml-px relative block z-1 w-full">
                <Menu.Button disabled={disabled} className={`${ statusStyles[currentStatus]} disabled:cursor-not-allowed w-full outline-none transition-all inline-flex gap-2 items-center justify-between px-4 py-2 shadow-sm text-base font-medium rounded-md`}>
                    <p className='text-nowrap'>{translateStatus[currentStatus]}</p>
                    {!disabled && <ChevronDownIcon className='h-5 w-5' />}
                </Menu.Button>
                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95">
                    <Menu.Items className={`w-full ${itemsPositionValues[itemsPosition]} z-50 absolute right-0 mt-2 min-w-[250px] w-[250px] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none`}>
                        <div className="py-1" >
                            {isContract ?  (statusListContract.map((status, i) => (
                                <Menu.Item key={i}>
                                    {({ active }) => (
                                        <button
                                            onClick={() => handleStatusChange(status)}
                                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block p-2 text-sm w-full text-left flex items-center gap-2`} >
                                            {translateStatus[status]}
                                            {currentStatus == status && (
                                                <CheckIcon className='w-4 h-4' />
                                            )}
                                        </button>
                                    )}
                                </Menu.Item>
                            ))) :  (statusArray.map((status, i) => (
                                <Menu.Item key={i}>
                                    {({ active }) => (
                                        <button
                                            onClick={() => handleStatusChange(status)}
                                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block p-2 text-sm w-full text-left flex items-center gap-2`} >
                                            {translateStatus[status]}
                                            {currentStatus == status && (
                                                <CheckIcon className='w-4 h-4' />
                                            )}
                                        </button>
                                    )}
                                </Menu.Item>
                            )))}
                        </div>
                    </Menu.Items>
                </Transition>
            </Menu>
        </div>
    )
}

StatusMenuButton.propTypes = {
    status: PropTypes.oneOf(statusList),
    onChange: PropTypes.func,
    isContract: PropTypes.bool,
    disabled: PropTypes.bool,
    itemsPosition: PropTypes.oneOf(itemsPositionList),
    customStatus: PropTypes.arrayOf(PropTypes.string),
    updateStatus: PropTypes.func
}

StatusMenuButton.defaultProps = {
    status: "pending",
    isContract: false,
    disabled: false,
    itemsPosition: "bottom",
    customStatus: null,
    updateStatus: null
}

export default StatusMenuButton;